import React, { useMemo } from "react";
import { Check, X } from "lucide-react";

/**
 * Password policy: ≥8 chars, ≥1 lowercase, ≥1 uppercase, ≥1 special.
 * Kept in sync with backend `_validate_password_strength`.
 */
export function checkPasswordPolicy(pw) {
  const p = pw || "";
  return {
    length: p.length >= 8,
    lower: /[a-z]/.test(p),
    upper: /[A-Z]/.test(p),
    special: /[^A-Za-z0-9]/.test(p),
  };
}

export function isPasswordStrong(pw) {
  const c = checkPasswordPolicy(pw);
  return c.length && c.lower && c.upper && c.special;
}

/**
 * Inline live checklist rendered below any password input.
 */
export default function PasswordStrengthIndicator({ password, className = "", compact = false }) {
  const checks = useMemo(() => checkPasswordPolicy(password), [password]);
  const items = [
    { key: "length", label: "At least 8 characters" },
    { key: "lower", label: "One lowercase letter (a-z)" },
    { key: "upper", label: "One uppercase letter (A-Z)" },
    { key: "special", label: "One special character (!@#$%…)" },
  ];
  const passed = items.filter((i) => checks[i.key]).length;
  const strength = passed === 4 ? "strong" : passed >= 2 ? "medium" : "weak";
  const strengthColor =
    strength === "strong" ? "bg-emerald-500"
    : strength === "medium" ? "bg-amber-500"
    : "bg-red-400";

  if (!password) return null;

  return (
    <div className={`mt-2 ${className}`} data-testid="password-strength-indicator">
      <div className="flex items-center gap-2 mb-2">
        <div className="flex-1 h-1 rounded-full bg-zinc-200 overflow-hidden">
          <div
            className={`h-full transition-all duration-200 ${strengthColor}`}
            style={{ width: `${(passed / 4) * 100}%` }}
          />
        </div>
        <span
          className="tag-mono text-[10px] uppercase tracking-widest text-zinc-500 font-bold"
          data-testid="password-strength-label"
        >
          {strength}
        </span>
      </div>
      {!compact && (
        <ul className="space-y-1">
          {items.map((it) => {
            const ok = checks[it.key];
            return (
              <li
                key={it.key}
                className={`flex items-center gap-1.5 text-xs tag-mono ${ok ? "text-emerald-700" : "text-zinc-500"}`}
                data-testid={`password-check-${it.key}`}
              >
                {ok ? <Check className="h-3 w-3 text-emerald-600" /> : <X className="h-3 w-3 text-zinc-400" />}
                {it.label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
